import { useEffect, type RefObject } from "react";

// Must match the attribute AthleteRow sets on skeleton rows.
export const UNLOADED_CLIENT_ID_ATTR = "data-unloaded-client-id";

// Prefetch a bit above/below the viewport so normal scrolling stays ahead of
// the coach. A fast jump still only loads whatever is near where they land —
// skipped rows stay as skeletons until they become visible.
const BUFFER_PX = 400;
const SCROLL_DEBOUNCE_MS = 100;

export const collectVisibleUnloadedClientIds = (
  root: ParentNode | null,
  bufferPx = BUFFER_PX
): string[] => {
  if (!root || typeof window === "undefined") return [];
  const top = -bufferPx;
  const bottom = window.innerHeight + bufferPx;
  const ids: string[] = [];
  root.querySelectorAll(`[${UNLOADED_CLIENT_ID_ATTR}]`).forEach((row) => {
    const rect = row.getBoundingClientRect();
    if (rect.bottom >= top && rect.top <= bottom) {
      const id = row.getAttribute(UNLOADED_CLIENT_ID_ATTR);
      if (id) ids.push(id);
    }
  });
  return ids;
};

/**
 * Loads configs for unloaded rows that are actually on screen (plus BUFFER_PX),
 * not the next page from the top of the table.
 *
 * IntersectionObserver is the cheap "something came into view" signal.
 * The ids themselves always come from geometry, and a debounced scroll /
 * scrollend / resize pass covers fast scrollbar jumps where IO can miss
 * intermediate rows — after the jump, whatever landed in the viewport is
 * still in the DOM and will be collected.
 */
export const useVisibleUnloadedRows = (
  rootRef: RefObject<HTMLElement | null>,
  onVisibleIds: ((ids: string[]) => void) | undefined,
  // Attribute set of skeleton rows; included in the effect so we re-observe
  // after search/filter or a batch resolving. Empty means nothing to watch.
  unloadedRowSignature: string
) => {
  useEffect(() => {
    if (!onVisibleIds || !unloadedRowSignature) return;
    const root = rootRef.current;
    if (!root) return;

    let debounceTimer: ReturnType<typeof setTimeout> | undefined;

    const flush = () => {
      const ids = collectVisibleUnloadedClientIds(root);
      if (ids.length) onVisibleIds(ids);
    };

    const flushDebounced = () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = setTimeout(flush, SCROLL_DEBOUNCE_MS);
    };

    const observer = new IntersectionObserver(flushDebounced, {
      rootMargin: `${BUFFER_PX}px 0px`,
    });
    root.querySelectorAll(`[${UNLOADED_CLIENT_ID_ATTR}]`).forEach((row) => {
      observer.observe(row);
    });

    window.addEventListener("scroll", flushDebounced, { passive: true });
    window.addEventListener("scrollend", flush);
    window.addEventListener("resize", flushDebounced);
    flush();

    return () => {
      observer.disconnect();
      window.clearTimeout(debounceTimer);
      window.removeEventListener("scroll", flushDebounced);
      window.removeEventListener("scrollend", flush);
      window.removeEventListener("resize", flushDebounced);
    };
  }, [onVisibleIds, rootRef, unloadedRowSignature]);
};
